import PropTypes from 'prop-types';

import Layout from 'components/layout';
import { availableSorts, DEFAULT_SORT } from 'utils/filters';

import { getSeoRelLinks } from 'components/paginator';
import { baseTranslations } from 'utils/translations';

import { getProducts } from 'actions/products';
import { filterTransformer, DEFAULT_LIMIT, getRealID, COOKIES } from 'utils/helpers';
import { initCMS } from 'utils/cms';
import { TIER_POSITIONS } from 'utils/constants/cms';

import { getSeo } from 'actions/general';

import Head from 'next/head';
import { ProductsListPage } from 'components/grid/product-list-page';
import { getDomainValue } from 'utils/domain';

function WithLayout({ defaultProps, children, breadcrumbI18n, camperlab, recycled, isApp, breadcrumbsBottom, breadcrumbs }) {
  if (isApp) {
    return children;
  }

  return (
    <Layout
      {...defaultProps}
      breadcrumbI18n={breadcrumbI18n}
      breadcrumbs={breadcrumbs}
      breadcrumbsBottom={breadcrumbsBottom}
      scrollArrow={true}
      camperlab={camperlab}
      recycled={recycled}
    >
      {children}
    </Layout>
  );
}

WithLayout.propTypes = {
  children: PropTypes.node,
  isApp: PropTypes.bool,
  defaultProps: PropTypes.any,
  breadcrumbI18n: PropTypes.object,
  camperlab: PropTypes.bool,
  recycled: PropTypes.bool,
  title: PropTypes.string,
  breadcrumbsBottom: PropTypes.bool,
};

export default function ProductsList({ defaultProps, hrefLangLinks, breadcrumbI18n, gtmProps, camperlab, metatags, recycled, isApp, ...rest }) {
  const hasStandalone = rest.tiersProps[TIER_POSITIONS.STANDALONE] !== null && rest.tiersProps[TIER_POSITIONS.STANDALONE] !== undefined;
  const hasTitleDescription = rest.title !== null && rest.title !== undefined && rest.description !== null && rest.description !== undefined;
  const isGridSeeAll = rest.filtersCount === 0 && !rest.hasFiltersUrl;

  return (
    <WithLayout
      defaultProps={{
        ...defaultProps,
        metatags,
        pageName: 'grid',
        gtmProps: { ...gtmProps, products: rest.firstProducts },
      }}
      isApp={isApp}
      breadcrumbs={getDomainValue({ camper: true, nnormal: true, camperlab: false })}
      breadcrumbI18n={getDomainValue({ camper: breadcrumbI18n, nnormal: breadcrumbI18n, camperlab: {} })}
      breadcrumbsBottom={hasStandalone || hasTitleDescription}
      camperlab={camperlab}
      recycled={recycled}
      scrollArrow={true}
    >
      <Head>
        {getSeoRelLinks(rest.initialPage, rest.totalPages, rest.urlCanonical, isGridSeeAll)}
        {hrefLangLinks?.map((l) => (
          <link key={`hreflang-${l.hrefLang}`} rel="alternate" hrefLang={l.hrefLang} href={l.url} />
        ))}
      </Head>
      <ProductsListPage {...rest} isApp={isApp} gtmProps={gtmProps} camperlab={camperlab} breadcrumbI18n={breadcrumbI18n} />
    </WithLayout>
  );
}

ProductsList.i18nPage = 'grid';

// probar props
export async function getServerSideProps(context) {
  const lang = context.locale;
  const { target, product, page, sort } = context.query;
  const targets = Object.keys(baseTranslations.target)
    .map((langCode) => baseTranslations.target[langCode])
    .reduce((a, b) => a.concat(b));
  const families = Object.keys(baseTranslations.product)
    .map((langCode) => baseTranslations.product[langCode])
    .reduce((a, b) => a.concat(b));

  if (!targets.includes(target) || !families.includes(product)) {
    return {
      notFound: true,
    };
  }

  const searchParams = { ...context.query };
  delete searchParams.target;
  delete searchParams.product;

  const filters = {};

  Object.keys(searchParams).forEach((searchKey) => {
    if (searchKey.startsWith('filter.')) {
      filters[searchKey] = searchParams[searchKey];
    }
  });

  const hasFiltersInURL = Object.keys(filters).length > 0;

  const resolvedUrl = context.resolvedUrl || '';
  try {
    const pageNumber = Number(page);
    const pageOffset = pageNumber > 1 ? (pageNumber - 1) * DEFAULT_LIMIT : 0;
    const appliedSort = availableSorts.includes(sort) && sort !== DEFAULT_SORT ? sort : false;
    const previousPagePromise =
      pageOffset === 0 ?
        Promise.resolve(null)
      : getProducts({
          context,
          lang,
          target,
          page: pageOffset - DEFAULT_LIMIT,
          filters,
          sort: appliedSort,
          product,
          square: getDomainValue({ camper: true, camperlab: false, nnormal: false }),
        });
    const nextPagePromise =
      pageOffset === 0 ?
        Promise.resolve(null)
      : getProducts({
          context,
          lang,
          target,
          page: pageOffset + DEFAULT_LIMIT,
          filters,
          sort: appliedSort,
          product,
          square: getDomainValue({ camper: true, camperlab: false, nnormal: false }),
        });

    const [response, previousPage, nextPage] = await Promise.all([
      getProducts({
        context,
        lang,
        target,
        page: pageOffset,
        filters,
        sort: appliedSort,
        product,
        seoContent: true,
        square: getDomainValue({ camper: true, camperlab: false, nnormal: false }),
      }),
      previousPagePromise,
      nextPagePromise,
    ]);

    const {
      products,
      total,
      availableFilters,
      currentList,
      currentListId,
      camperlab,
      title,
      description,
      urlCanonical,
      urlCanonicalGTM,
      seoContent,
      rewalk,
      relatedCollectionId,
      urlRelatedCollection,
    } = response;
    // No devolver un 404, ya que aunque la grid esté vacía es posible que haya Tiers
    /* if (products.length === 0) {
      return {
        notFound: true,
      };
    } */
    let baseAvailable = availableFilters;
    if (hasFiltersInURL) {
      const getProductsParams = {
        context,
        lang,
        target,
        product,
        page: 0,
        limit: 1,
        filters: null,
      };

      const collectionFromUrl = filters['filter.collection'];
      const sustainabilityFromUrl = filters['filter.sustainability'];

      if (collectionFromUrl) {
        getProductsParams.filters = {
          'filter.collection': collectionFromUrl,
        };
      }
      if (sustainabilityFromUrl) {
        getProductsParams.filters = {
          'filter.sustainability': sustainabilityFromUrl,
        };
      }
      const baseRequest = await getProducts(getProductsParams);
      baseAvailable = baseRequest.availableFilters;
    }
    const targetRealID = getRealID('target', target);
    const familyRealID = getRealID('product', product);

    // //////////////// Tiers

    // [TODO]: Temporal. Determinar que tipo de tier es:
    //         - collection, line, concept, typology, sustainability....
    // En funcion del rewrites y el filter que traiga.
    let tierType = 'grid';
    let cmsCode = '';
    let gridId = 'see-all';

    if (hasFiltersInURL) {
      const collectionFromUrl = filters['filter.collection'];
      const sustainabilityFromUrl = filters['filter.sustainability'];
      const typologyFromUrl = filters['filter.typology'];
      const conceptFromUrl = filters['filter.concept'];

      if (typologyFromUrl) {
        tierType = 'typology';
        cmsCode = typologyFromUrl;
        gridId = '';
      } else if (sustainabilityFromUrl) {
        tierType = 'sustainability';
        cmsCode = sustainabilityFromUrl;
        gridId = '';
      } else if (collectionFromUrl) {
        tierType = 'collection';
        cmsCode = collectionFromUrl;
        gridId = '';
      } else if (conceptFromUrl) {
        tierType = 'concept';
        cmsCode = conceptFromUrl;
        gridId = '';
      }
    }
    const hasSubgrid = relatedCollectionId !== null && relatedCollectionId !== undefined && relatedCollectionId !== '';
    let subgridUrlCollection = '';
    let subgridProducts = [];
    if (hasSubgrid) {
      const resolvedUrlWithoutQuery = resolvedUrl.split('?')[0];
      subgridUrlCollection = urlRelatedCollection || `/${lang}${resolvedUrlWithoutQuery}?filter.collection=${relatedCollectionId}`;
      const subgridResponse = await getProducts({
        context,
        lang,
        target,
        page: 0,
        limit: 8,
        filters: { 'filter.collection': relatedCollectionId },
        sort: false,
        product,
        seoContent: false,
        square: getDomainValue({ camper: true, camperlab: false, nnormal: false }),
      });
      subgridProducts = subgridResponse.products.slice(0, 8);
    }
    // //////////////// Fin Tiers
    const { defaultProps, tiers, cmsSeoContent } = await initCMS({
      context,
      locale: lang,
      tierParams: [tierType, [cmsCode], targetRealID, familyRealID, gridId],
      tnmSelectedTarget: targetRealID,
      seoContent: camperlab ? null : { target: targetRealID },
      camperlab,
    });

    const appliedFilters = Object.keys(filters).length > 0 ? filterTransformer.decode(filters, availableFilters) : {};
    const filtersCount = Object.keys(appliedFilters).length > 0 ? Object.keys(appliedFilters).reduce((a, b) => appliedFilters[b].length + a, 0) : 0;

    const gtmProps = {
      target,
      targetRealID,
      product,
      familyRealID,
      currentList: currentList ?? '',
      currentListId,
      camperlab,
      currentListName: title ?? '',
      page: pageNumber - 1,
      url: urlCanonicalGTM || null,
    };

    const queryParams = [
      `profile=${lang}`,
      `family=${familyRealID || '1'}`,
      `gender=${targetRealID || 'X'}`,
      `option=${targetRealID || 'X'}`,
      'page=PRODUCTOS',
    ];
    const collectionFilter = filters['filter.collection'];
    const conceptFilter = filters['filter.concept'];
    const typologyFilter = filters['filter.typology'];
    const sustainabilityFilter = filters['filter.sustainability'];
    if (collectionFilter) {
      queryParams.push(`specialCollection=${collectionFilter}`);
    }
    if (conceptFilter) {
      queryParams.push(`collection=${conceptFilter}`);
    }
    if (typologyFilter) {
      queryParams.push(`typology=${typologyFilter}`);
    }
    if (sustainabilityFilter) {
      queryParams.push(`sustainability=${sustainabilityFilter}`);
    }
    const metatags = await getSeo(context, queryParams);
    const breadcrumbI18n = {
      target: targetRealID.toLocaleLowerCase() || null,
      family: familyRealID || null,
    };

    const originalCta = {};
    if (tiers?.standalone && tiers?.standalone?.ctaText) {
      originalCta.text = tiers.standalone.ctaText;
      if (tiers.standalone.ctaTextProps) {
        originalCta.fontColor = tiers.standalone.ctaTextProps.fontColor || tiers?.textProps?.fontColor || '#000000';
        tiers.standalone.ctaTextProps.fontColor = '#ffffff00';
      }
    }
    const isApp = context?.req?.cookies[COOKIES.APP_IPAD] === 'true' || false;
    return {
      props: {
        defaultProps,
        hrefLangLinks: null,
        currentList: currentList || null,
        firstProducts: products,
        previousPageProducts: previousPage?.products ?? null,
        nextPageProducts: nextPage?.products?.length > 0 ? nextPage.products : null,
        total,
        availableFilters,
        baseAvailable,
        appliedFilters,
        defaultSort: availableSorts.includes(sort) ? sort : DEFAULT_SORT,
        filtersCount,
        hasFiltersInURL,
        gtmProps,
        tierParams: [tierType, [cmsCode], targetRealID, familyRealID, gridId],
        tiersProps: !isApp && tiers ? tiers : {},
        initialPage: pageNumber || 1,
        camperlab,
        title: title || null,
        description: description || null,
        metatags,
        breadcrumbI18n,
        urlCanonical,
        originalCta,
        resolvedUrl,
        recycled: rewalk ?? false,
        seoContent:
          seoContent ?
            {
              ...seoContent,
              cms:
                cmsSeoContent ?
                  {
                    ...cmsSeoContent,
                    gridName: currentList ?? title ?? '',
                  }
                : null,
            }
          : null,
        subgridProducts,
        subgridUrlCollection,
        isApp,
      },
    };
  } catch (e) {
    console.error(e);
    context.res.statusCode = 500;
    return {
      props: {
        error: `${context.resolvedUrl} SSR Error: ${e}`,
        permanent: false,
      },
    };
  }
}
